import React, { useState } from 'react';
import { InputContainer, InputLabel, TabBody, JsonBody, LineSeparator } from './styles';

import { SINGLE_SIGN_ON_SETTINGS as trans } from './TranslationValues';
import { Input, Icon, Tooltip, message, Checkbox, Switch, Popconfirm } from 'antd';
import { XMLParser } from 'fast-xml-parser';
import { withTranslation } from 'react-i18next';
import ActionButton from '../../components/ActionButton';

const { TextArea } = Input;

const NAMESPACE = ['SINGLE_SIGN_ON_SETTINGS'];

const isValidXML = xmlString => {
  try {
    const parser = new XMLParser({
      ignoreAttributes: false,
      allowBooleanAttributes: false,
      trimValues: false,
    });
    const result = parser.parse(xmlString);
    return typeof result === 'object' && result !== null && Object.keys(result).length > 0;
  } catch {
    return false;
  }
};

const SAMLPanel = ({ t, isVisible, onSave, samlConfig, setConfigModal, directoryEnabled = false, handleEnableDirectory, currentSavedConfig }) => {
  const [defaultSamlConfig, setDefaultSamlConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [editSaml, setEditSaml] = useState(false);
  const [togglePopconfirm, setTogglePopconfirm] = useState(false);

  const tooltipContent = (
    <div style={{ fontSize: '12px' }}>
      You can use any of the following as your tenant name or identifier:
      <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'disc', textAlign: 'left' }}>
        <li>Email domain</li>
        <li>Organization ID or slug</li>
        <li>Team ID or slug</li>
        <li>Other unique identifiers</li>
      </ul>
    </div>
  );

  const handleConfigChange = ({ target: { name, value } }) => {
    setDefaultSamlConfig(prev => {
      console.log(prev);
      return { ...prev, [name]: value };
    });
  };

  const checkSamlConfig = () => {
    if (!defaultSamlConfig.tenantName || !defaultSamlConfig.idpXML) {
      return message.warning('Tenant name and IDP XML are required');
    }
    if (isValidXML(defaultSamlConfig.idpXML)) {
      const method = currentSavedConfig === 'oidc' ? 'put' : editSaml ? 'put' : 'post';
      setLoading(true);
      onSave(
        method,
        {
          tenant: defaultSamlConfig.tenantName,
          encodedRawMetadata: btoa(defaultSamlConfig.idpXML),
        },
        () => {
          setEditSaml(false);
          setLoading(false);
        },
      );
    } else {
      message.warning('IDP XML is not valid');
    }
  };

  const handleEditSaml = () => {
    setDefaultSamlConfig({ tenantName: samlConfig.tenant });
    setEditSaml(true);
  };

  return (
    <TabBody isVisible={isVisible}>
      {samlConfig?.tenant && samlConfig.idpMetadata && !editSaml ? (
        <>
          <InputContainer full>
            <InputLabel>{t(trans.SSO_TENANT_NAME.key, trans.SSO_TENANT_NAME.value)}</InputLabel>
            <Input size="large" disabled value={samlConfig.tenant} autoComplete={false} />
          </InputContainer>
          <InputContainer full>
            <InputLabel>{'IdP Metadata'}</InputLabel>
            <JsonBody>{JSON.stringify(samlConfig.idpMetadata, null, 4)}</JsonBody>
          </InputContainer>
          <div style={{ marginTop: '28px' }}>
            <ActionButton
              label={'Edit SAML Configurations'}
              onClick={handleEditSaml}
            />
          </div>
          <div style={{ margin: '28px' }}>
          <Popconfirm
            title={'Are you sure you want to enable Directory Sync? Once enabled, it cannot be disabled.'}
            okText={'Enable'}
            cancelText={'Cancel'}
            placement="top"
            trigger="click"
            visible={togglePopconfirm}
            onConfirm={() => {
              handleEnableDirectory(true)
              setTogglePopconfirm(false);
            }}
            onCancel={() => setTogglePopconfirm(false)}
            overlayStyle={{ width:350 }}
          >
            <Switch
              className="ds-switch"
              checkedChildren="Directory Sync Enabled"
              unCheckedChildren="Directory Sync Disabled"
              checked={directoryEnabled || false}
              disabled={directoryEnabled || false}
              onChange={checked => setTogglePopconfirm(true) }
            />
            </Popconfirm>
          </div>
        </>
      ) : (
        <>
          <InputContainer full>
            <InputLabel>
              {t(trans.SSO_TENANT_NAME.key, trans.SSO_TENANT_NAME.value)}{' '}
              <Tooltip title={tooltipContent}>
                <Icon type="info-circle" />
              </Tooltip>
            </InputLabel>
            <Input
              name="tenantName"
              size="large"
              onChange={handleConfigChange}
              defaultValue={samlConfig?.tenant || ''}
              autoComplete={false}
            />
            <p className="help-text">
              Unique identifier for the tenant to which this SSO connection is linked
            </p>
          </InputContainer>
          <InputContainer full>
            <InputLabel>{t(trans.RAW_IDP_XML.key, trans.RAW_IDP_XML.value)}</InputLabel>
            <TextArea
              name="idpXML"
              size="large"
              autoSize={{ minRows: 3, maxRows: 10 }}
              onChange={handleConfigChange}
            />
            <p className="help-text">
              Paste the raw XML metadata obtained from your SAML provider.
            </p>
          </InputContainer>
          <div style={{ margin: '20px' }}>
            <ActionButton
              label={editSaml ? 'Update SAML Configurations' : 'Configure SAML'}
              onClick={checkSamlConfig}
              loading={loading}
            />
          </div>
        </>
      )}
      <div className="config-link-container">
        Share the&nbsp;
        <span className="config-link" onClick={() => setConfigModal('saml')}>
          SAML Configuration
        </span>
        { directoryEnabled && ` & `}
        { directoryEnabled &&  <span className="config-link" onClick={() => setConfigModal('directory')}>
             Directory Configuration
          </span>
        }
        &nbsp;information with Your Identity Provider (IdP){' '}
      </div>
    </TabBody>
  );
};

export default withTranslation(NAMESPACE)(SAMLPanel);
